<template>
   <!--Page alert + loader-->
   <PageStatus :alert="defaultAlert" :table-data="tableData" />


   <!--Import Data-->
   <ImportModal
      :form-import-data="formImportData"
      :alert="defaultAlert"
      @file-change="onFileImportChange"
      @import="importRecords"
      @download-template="downloadImportTemplate"
   >
      <!--Import Notes-->
      <div id="accordion">
         <div class="card">
            <div class="card-header">
               <a class="btn" data-bs-toggle="collapse" href="#collapseOne">
                  Import Process
               </a>
            </div>
            <div id="collapseOne" class="collapse show" data-bs-parent="#accordion">
               <div class="card-body">
                  <ol>
                     <li>Click <b>Download Import Template</b> for the right columns, or use a file from <b>Export to Excel</b>: it has the same columns.</li>
                     <li>All columns except <b>From name</b> are required. <b>Port</b> is a whole number, e.g. 587 (tls) or 465 (ssl). The password is saved exactly as typed.</li>
                     <li><b>Email provider</b>: one of the names on the form, e.g. Gmail, Outlook or Custom Email.</li>
                     <li>Only one setting is active per company and branch: the <b>last row</b> of the file becomes the active setting, and all others become inactive.</li>
                     <li>If no row is valid, nothing is saved and your current email setting keeps working.</li>
                     <li>Settings are imported into the company and branch you are logged into. Switch to a specific branch first.</li>
                     <li>The file holds passwords: keep it safe and delete it after importing.</li>
                  </ol>
               </div>
            </div>
         </div>
      </div>
      <!--End Import Notes-->
   </ImportModal>
   <!--Import Data-->

         <!--Filter Data Modal (shared: FilterModal.vue)-->
   <FilterModal
      :draft="filterDraft"
      v-model:search-by="tableData.searchBy"
      :view-options="viewOptions"
      :search-by-options="searchByOptions"
      :alert="defaultAlert"
      @search-by-change="changePlaceholder()"
      @apply="applyFilters"
      @clear="clearFilters"
   />
   <!--End Filter Data Modal-->

   
   <!-- The Modal -->
   <RecordFormModal
      :title="isEditing ? 'Update Email Settings' : 'Add Email Settings'"
      title-icon="bi bi-envelope"
      :is-editing="isEditing"
      :is-saving="isSaving"
      :alert="defaultAlert"
      @save="saveOrEditRecord"
      @sample="addSampleData"
      @reset="resetForm"
   >
      <!--Below id data id. User id is accessed directly in computed properties inside formData_fields function-->
      <HiddenField v-model="formInputs.id" />

      <!-- Row 1: Email & Password -->
      <div class="row g-3 mb-0">
         <FormField
            v-model="formInputs.from_email"
            col="col-md-6"
            type="email"
            label="From Email"
            icon="fa fa-envelope"
            placeholder="e.g. info@yourcompany.com"
            tooltip="The email address the system sends from"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
         <PasswordField
            v-model="formInputs.from_password"
            col="col-md-6"
            name="from_password"
            label="Password"
            icon="fa fa-key"
            autocomplete="new-password"
            :placeholder="isEditing ? 'Leave blank to keep the current one' : ''"
            :tooltip="isEditing ? 'Leave blank to keep the current password' : 'The email password or app password'"
            :required="!isEditing"
            @input="handleInputChange"
         />
      </div>

      <!-- Row 2: Email Provider -->
      <div class="row g-3 mb-0">
         <SelectField
            v-model="formInputs.email_provider"
            col="col-md-12"
            name="email_provider"
            label="Email Provider"
            icon="fa fa-envelope-o"
            :options="emailProviders"
            placeholder="Select..."
            tooltip="Please select Email Provider"
            :submitted="submitted"
            required
            @change="onProviderChange"
         />
      </div>

      <!-- Row 3: From Name & Server -->
      <div class="row g-3 mb-0">
         <FormField
            v-model="formInputs.from_name"
            col="col-md-6"
            label="From Name"
            icon="fa fa-id-card-o"
            placeholder="e.g. wihfocus Media"
            tooltip="The name people see as the sender"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
         <FormField
            v-model="formInputs.from_host"
            col="col-md-6"
            label="Server Name"
            icon="fa fa-server"
            :placeholder="customPlaceholders.from_host"
            tooltip="Enter your Server Name"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
      </div>

      <!-- Row 4: Port & SSL/TLS -->
      <div class="row g-3 mb-0">
         <FormField
            v-model="formInputs.from_port"
            col="col-md-6"
            type="number"
            min="1"
            step="1"
            label="Port"
            icon="fa fa-plug"
            :placeholder="customPlaceholders.from_port"
            tooltip="Usually 587 (tls) or 465 (ssl)"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
         <SelectField
            v-model="formInputs.from_smtp_secure"
            col="col-md-6"
            name="from_smtp_secure"
            label="SSL/TLS"
            icon="fa fa-shield"
            :options="sslOptions"
            :placeholder="customPlaceholders.from_smtp_secure || 'Select...'"
            tooltip="Please choose tls or ssl"
            :submitted="submitted"
            required
            @change="$clearValidationAlert('#recordForm')"
         />
      </div>
   </RecordFormModal>   


   <h4 class="sa-core-color fw-semibold mb-2">
      <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
   </h4>
   <!--Tables-->
   <div class="card glass mt-0 bg-sa-card">
      <div class="card-body bg-sa-card-body p-0">
         
                  <!-- Table action row (shared: TableToolbar.vue) -->
         <TableToolbar
            can-add
            :filter-count="activeFilterCount"
            :columns="tableColumns"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="tableData.selectedRecordIds.length"
            :deleted-view="tableData.recordFilter === 'deletedone'"
            :can-delete="$hasPermission('email_settings.delete')"
            @add="clearForm()"
            @filter="openFilters"
            @clear-selection="clearSelection"
            @bulk-action="runBulkAction"
         >
            <!-- More menu -->
            <template #more>
               <li><h6 class="dropdown-header">Data</h6></li>
               <li>
                  <button type="button" class="dropdown-item" data-bs-toggle="modal" data-bs-target="#importModal">
                     <i class="fa fa-upload fa-fw me-1"></i> Import
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                     <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                     <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                     <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                  </button>
               </li>
            </template>
         </TableToolbar>
         <!-- Table Search Panel -->

         <SearchBox
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
         />
         <!-- What the table is currently showing -->
         <ActiveFilters
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
         />
         <!-- Table -->
         <div class="table-responsive">
            
            <table
    class="table table-hover align-middle mb-0"
    id="bootstrapTable"
    tabindex="0"
    @keydown.up="$moveRowSelection(-1, $event)"
    @keydown.down="$moveRowSelection(1, $event)"
    @keydown.enter="$onTableEnter($event)"
>
      <thead>
      <tr>
         <th>
            <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
         </th>
         <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
         <th class="button hide-sm-td">No</th>
         <SortableTh v-if="isColumnVisible('from_email')" column="from_email" :table-data="tableData" @sort="orderBy">From Email</SortableTh>
         <SortableTh v-if="isColumnVisible('email_provider')" column="email_provider" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Provider</SortableTh>
         <SortableTh v-if="isColumnVisible('from_host')" column="from_host" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Host</SortableTh>
         <SortableTh v-if="isColumnVisible('from_port')" column="from_port" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Port</SortableTh>
         <SortableTh v-if="isColumnVisible('from_smtp_secure')" column="from_smtp_secure" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Security</SortableTh>
         <SortableTh v-if="isColumnVisible('is_active')" column="is_active" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Status</SortableTh>
         <th class="button">Action</th>
      </tr>
   </thead>

   <tbody>
      <template v-for="(record, index) in tableData.records" :key="record.id">

         <!-- Main row -->
         <tr class="clickable-row"
             :data-record-id="record.id"
             :class="{ 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) }"
             @click="$onRowClick(record, $event)"
             @dblclick="$onRowDblClick(record, $event)"
         >
            <td>
               <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
            </td>
            <td class="expand-cell hide-sm-td"
                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                @click="$toggleDetails(record.id)">
               <i class="fa fa-lg expand-icon ms-1"
                  :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                  :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
            </td>
            <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
            <td v-if="isColumnVisible('from_email')">
               <!-- Phones: + / − and the number go in front of the email (their columns are hidden) -->
               <span
                  class="show-sm-inline expand-cell me-2"
                  :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                  @click="$toggleDetails(record.id)"
               >
                  <i class="fa fa-lg expand-icon"
                     :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                     :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
               </span>
               <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>{{ record.from_email }}
            </td>
            <td v-if="isColumnVisible('email_provider')" class="hide-sm-td">{{ record.email_provider }}</td>
            <td v-if="isColumnVisible('from_host')" class="hide-sm-td">{{ record.from_host }}</td>
            <td v-if="isColumnVisible('from_port')" class="hide-sm-td">{{ record.from_port }}</td>
            <td v-if="isColumnVisible('from_smtp_secure')" class="hide-sm-td">{{ record.from_smtp_secure }}</td>
            <td v-if="isColumnVisible('is_active')" class="hide-sm-td">
               <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-secondary'">
                  {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
               </span>
            </td>
            <td>
               <RowActions
                  :record="record"
                  :modal-id="'deleteCrudModal' + record.id"
                  :can-soft-delete="$hasPermission('email_settings.delete')"
                  :can-restore="$hasPermission('email_settings.delete')"
                  :can-delete-permanently="$hasPermission('email_settings.delete')"
                  @edit="editRecord"
                  @delete-type="setDeleteType"
               />

               <!--Delete Modal-->
              <DeleteModal
                   :modal-id="'deleteCrudModal' + record.id"
                   :title="deleteModalTitle"
                   :message="deleteModalMessage"
                   @confirm="deleteRecord(record.id)"
               />
               <!--End Delete Modal-->
            </td>
         </tr>

         <!-- Hidden expanded row -->
         <tr class="collapse-row">
            <td class="p-0"></td>
            <td class="p-0 hide-sm-td"></td>
            <td colspan="8" class="p-0">
               <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                  <div class="p-3 details-list">
                     <div><i class="fa fa-hashtag"></i><strong>Setting Number:</strong> {{ record.email_setting_number }}</div>
                     <div><i class="fa fa-user"></i><strong>Creator Name:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
                     <div><i class="fa fa-building"></i><strong>Creator Company:</strong> {{ record.company_name }}</div>
                     <div><i class="fa fa-sitemap"></i><strong>Creator Branch:</strong> {{ record.branch_name }}</div>

                     <!-- Shown on phones, or always when the column is hidden (Columns button) -->
                     <div :class="{ 'show-sm-td': isColumnVisible('from_email') }"><i class="fa fa-envelope"></i><strong>From Email:</strong> {{ record.from_email }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('email_provider') }"><i class="fa fa-envelope-o"></i><strong>Provider:</strong> {{ record.email_provider }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('from_host') }"><i class="fa fa-server"></i><strong>Host:</strong> {{ record.from_host }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('from_port') }"><i class="fa fa-plug"></i><strong>Port:</strong> {{ record.from_port }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('from_smtp_secure') }"><i class="fa fa-lock"></i><strong>Security:</strong> {{ record.from_smtp_secure }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('is_active') }">
                        <i class="fa fa-toggle-on"></i><strong>Status:</strong>
                        <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-secondary'">
                           {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
                        </span>
                     </div>

                     <!-- Always in details -->
                     <div><i class="fa fa-id-card-o"></i><strong>From Name:</strong> {{ record.from_name }}</div>
                     <div v-if="$hasPermission('view-location')"><i class="fa fa-location-arrow"></i><strong>Created From:</strong> {{ record.current_location }}</div>

                     <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ this.$ConvertToLocalDate(record.date_created) }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ this.$ConvertToLocalDate(record.date_updated) }}</div>
                     <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                  </div>
               </div>
            </td>
         </tr>

      </template>

      <!-- No rows: blank while loading, then the empty message in the middle -->
      <tr v-if="tableData.records.length === 0" class="table-filler">
         <td colspan="10" class="text-center align-middle" :style="{ height: fullPageHeight() }">
            <TableEmptyState
               v-if="!tableLoading"
               :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
               :can-add="true"
               :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No email settings yet'"
               :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Add one so the app can send emails (password resets, notifications).'"
               icon="fa fa-envelope"
               @add="clearForm()"
               @clear="clearAllFilterChips"
            />
         </td>
      </tr>
   </tbody>

   <tfoot class="table-light">
      <tr>
         <td colspan="10">
            <!-- Pagination (hidden when there are no rows; its space is kept) -->
            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
            <Pagination
               v-else
               :currentPage="tableData.currentPage"
               :totalPages="tableData.totalPages"
               :hasNextPage="tableData.hasNextPage"
               :totalRecords="tableData.totalRecords"
               :startRecord="tableData.startRecord"
               :endRecord="tableData.endRecord"
               :showFullPagination="tableData.totalRecords > 0"
               :lastKnownPage="lastKnownPage"
               @page-changed="fetchRecords"
               @loaderMessage="updateLoaderMessage"
               @last-page-request="goToLastPage"
            />
         </td>
      </tr>
   </tfoot>
</table>
         </div>
      </div>
   </div>
   <!--End Tables-->
</template>
<script>
import vSelect from "vue-select"
import "vue-select/dist/vue-select.css"

import PageStatus from "../../../components/admin/PageStatus.vue";
import TableToolbar from "../../../components/admin/TableToolbar.vue";
import FilterModal from "../../../components/admin/FilterModal.vue";
import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
import Pagination from "../../../components/admin/Pagination.vue";
import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
import SortableTh from "../../../components/admin/SortableTh.vue";
import DeleteModal from "../../../components/admin/DeleteModal.vue";
import RowActions from "../../../components/admin/RowActions.vue";
import SearchBox from "../../../components/admin/SearchBox.vue";
import ImportModal from "../../../components/admin/ImportModal.vue";
import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
import FormField from "../../../components/admin/FormField.vue";
import SelectField from "../../../components/admin/SelectField.vue";
import PasswordField from "../../../components/admin/PasswordField.vue";
import HiddenField from "../../../components/admin/HiddenField.vue";
import emailSettingsMixin from "./mixins/emailSettingsMixin.js";
import tableMixin from "@/mixins/tableMixin.js";
import axios from 'axios';


export default {
   name: 'EmailSettings',
   mixins: [tableMixin, emailSettingsMixin], // shared table methods first, then EmailSettings' own
   components: {
      vSelect,
      PageStatus,
      TableToolbar,
      FilterModal,
      ActiveFilters,
      Pagination,
      TableEmptyState,
      SortableTh,
      DeleteModal,
      RowActions,
      SearchBox,
      ImportModal,
      RecordFormModal,
      FormField,
      SelectField,
      PasswordField,
      HiddenField,
   },
   data() {
      return {
         // Standard data (isSaving, tableData, defaultAlert, formImportData ...) comes from tableMixin
         standardPageSetup: true,          // tableMixin sets up Enter-to-save and table shortcuts
         apiUrl: "/settings/api_email_settings.php", // used by tableMixin's fetchRecords, delete, import, export
         importTemplateFileName: "email_settings_import_template.xlsx",
         columnsKey: "emailsettings",      // saves this page's hidden columns (Columns button)
         formInputs: this.getDefaultFormInputs(),
         customPlaceholders: {
            from_host: "",
            from_port: "",
            from_smtp_secure: ""
         },
         exportFileName: "emailSettingsExport", // name of the exported file
         pageIcon: 'fa fa-envelope', // same icon as this page in the sidebar
         // Filter window: View
         viewOptions: [
            { label: "All Records", value: "all" },
            { label: "Records By Me", value: "byme" },
            { label: "Records Deleted", value: "deletedone" },
         ],
         // Filter window: Search By
         searchByOptions: [
            { label: "From Email", value: "from_email" },
            { label: "From Name", value: "from_name" },
            { label: "Email Provider", value: "email_provider" },
            { label: "Host", value: "from_host" },
            { label: "Port", value: "from_port" },
            { label: "Security (SSL/TLS)", value: "from_smtp_secure" },
         ],
         sslOptions: ['tls', 'ssl'], // SSL/TLS dropdown
         emailProviders: [
            'Custom Email',
            'Gmail',
            'Outlook',
            'Yahoo',
            'iCloud',
            'Proton',
            'Zoho',
            'GMX',
            'Yandex',
            'Mail.com',
            'Tutanota'
         ]
      };
   },
   async created() {      
      //alert(this.$store.state.isLoggedIn);
      //this.retriveUserDetails();           
      await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
      this.$checkIfCanFetchLocation();//Only applies if location is enabled for user. If not Log user out for him to enable location 
   },
   computed: {
        // Page heading that follows the chosen filter.
        pageTitle() {
            const titles = {
                all: "Email Settings",
                byme: "Email Settings By Me",
                deletedone: "Email Settings Deleted Records",
            };
            return titles[this.tableData.recordFilter] || "Email Settings";
        },
                // Columns the user can show or hide (Columns button)
        tableColumns() {
            return [
                { key: "from_email", label: "From Email" },
                { key: "email_provider", label: "Provider" },
                { key: "from_host", label: "Host" },
                { key: "from_port", label: "Port" },
                { key: "from_smtp_secure", label: "Security" },
                { key: "is_active", label: "Status" },
            ];
        },
    },
   methods: {
      getDefaultFormInputs(){
         return {
            //Form Data
            id: "",
            email_provider: "",
            from_email: "",
            from_password: "",
            from_name: "",
            from_host: "",
            from_port: "",
            from_smtp_secure: ""
         };
      },
            // Table defaults: the standard ones (tableMixin) + this page's own
      getDefaultTableData(){
         return {
            ...this.baseTableData(),
            searchBy: 'from_email',
            searchPlaceHolderText: 'Search by From Email',
         };
      },
      addSampleData() {
         this.formInputs = {
            ...this.getDefaultFormInputs(),
            email_provider: "Gmail",
            from_email: "info@wihfocus.com",
            from_password: "abcd efgh ijkl mnop", // placeholder app password
            from_name: "wihfocus Media",
            from_host: "smtp.gmail.com",
            from_port: "587",
            from_smtp_secure: "tls",
         };

         this.submitted = false;
         this.$clearValidationStates("#recordForm");
         this.$scrollToTopModal("recordFormModal");
      },
      saveOrEditRecord() {       
      if (this.isSaving) return;
      this.isSaving = true;

      try {  
         this.$scrollToTopModal("recordFormModal"); 
         this.submitted = true;
         const formIsValid = this.$validateForm('#recordForm');
         if (!formIsValid) { 
          this.$playErrorSound();
               this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
              this.isSaving = false;
              return;
        }

         this.handleAlert("starting","Please wait submiting","info","modal");

         let formData = this.formData_fields();

         if (!this.formInputs.id) {
            this.tableData.currentPage = 1;
         }

         const start = performance.now(); // Start time
         // Perform the POST request
         axios.post(this.apiUrl, formData, this.$handleConfig())
            .then((response) => {     
               if (response.data.status =='success') {
                  const end = performance.now(); // End time
                  const duration = end - start;
                  console.log("Time taken to receive:" + duration.toFixed(2));
                  this.handleSaveOrEditSuccess(response);
               } else {
                   this.handleError("starting",response.data.message[0],"danger","modal");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","modal");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","modal");
                }
               //this.handleError("starting",error,"danger","modal");
             })
    .finally(() => {
                this.isSaving = false;
            });} catch (error) {
            this.isSaving = false;
            throw error;
        }
      },
      formData_fields(){
         let formData = new FormData();
         formData.append("id", this.formInputs.id);
         formData.append("email_provider", this.formInputs.email_provider);
         formData.append("from_email", this.formInputs.from_email);
         formData.append("from_password", this.formInputs.from_password);
         formData.append("from_name", this.formInputs.from_name);
         formData.append("from_host", this.formInputs.from_host);
         formData.append("from_port", this.formInputs.from_port);
         formData.append("from_smtp_secure", this.formInputs.from_smtp_secure);
         formData.append("latitude", this.$store.state.latitude);
         formData.append("longitude", this.$store.state.longitude);
         formData.append('action', 'saveOrEditRecord');
         return formData;
      },
      editRecord(records) {    
         this.isEditing = true;     
         this.formInputs.id = records.id ?? '';
         this.formInputs.email_provider = records.email_provider ?? '';
         //$(this.$refs.email_provider).val(this.formInputs.email_provider).trigger('change');

         this.formInputs.from_email = records.from_email ?? '';
         this.formInputs.from_password = ''; // never sent to the browser: blank keeps the saved one
         this.formInputs.from_name = records.from_name ?? '';
         this.formInputs.from_host = records.from_host ?? '';
         this.formInputs.from_port = records.from_port ?? '';
         this.formInputs.from_smtp_secure = records.from_smtp_secure ?? '';
         this.$clearValidationStates('#recordForm');
      },
   },
};
</script>